import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberPopover from '@mylighthouse/prism-ember/components/ember-popover';
import { or } from 'ember-truth-helpers';
import GeneralEventName from 'lumen/components/general/event-name';
import type { DayEvent } from 'lumen/components/hotel/events/calendars/calendar-events';
import HotelEventsTooltipsTooltipEvent from 'lumen/components/hotel/events/tooltips/tooltip-event';
import { EVENTS_STATUSES } from 'lumen/models/event';

interface HotelEventsCalendarsDaysContentEventBarSignature {
  Args: {
    data: DayEvent;
    ignorePositioning?: boolean;
    isLoading: boolean;
    refreshEvents: () => void;
  };
}

export default class HotelEventsCalendarsDaysContentEventBar extends Component<HotelEventsCalendarsDaysContentEventBarSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  /** A local state is kept per component as otherwise events spanning multiple rows have duplicate popups open */
  @tracked isPopoverVisible = false;

  get event(): DayEvent['event'] {
    return this.args.data.event;
  }

  /**
   * CSS Grid placement: position the event bar within the week grid.
   * grid-column places it at the correct day(s), grid-row at the correct event slot.
   */
  get gridStyle(): ReturnType<typeof htmlSafe> | undefined {
    if (this.args.ignorePositioning) {
      return undefined;
    }

    const { dayNo, length, order } = this.args.data;
    const colStart = dayNo + 1; // grid columns are 1-indexed
    const row = order + 2; // +1 for day-header row, +1 for 1-indexing

    return htmlSafe(
      `grid-column: ${colStart} / span ${length}; grid-row: ${row};`,
    );
  }

  get isCancelled(): boolean {
    return this.event.isCancelled;
  }

  get isNew(): boolean {
    return (
      this.event?.isNewEvent &&
      this.event?.status !== EVENTS_STATUSES.DECLINED &&
      this.event?.status !== EVENTS_STATUSES.ACCEPTED
    );
  }

  get isPastEvent(): boolean {
    const isOngoingEvent =
      this.event?.startDate.isBefore(new Date(), 'day') &&
      this.event?.endDate.isAfter(new Date(), 'day');

    return this.args.data?.date.isBefore(new Date(), 'day') && !isOngoingEvent;
  }

  get isPopoverActive(): boolean {
    return this.event && this.event.isPopoverActive;
  }

  get isRealEnd(): boolean {
    return (
      this.event &&
      !this.event.endDate.isAfter(this.args.data?.date.endOf('week'), 'day')
    );
  }

  get isRealStart(): boolean {
    return (
      this.event && this.args.data?.date.isSame(this.event?.startDate, 'day')
    );
  }

  get isRejected(): boolean {
    return this.event.isDeclined;
  }

  get newTag(): string {
    return `(${this.intl.t('general.new').toLocaleUpperCase()})`;
  }

  @action
  async acceptEvent(): Promise<void> {
    this.event.status = EVENTS_STATUSES.ACCEPTED;
    await this.saveEvent();
    this.usageAnalytics.track('Event Accepted', {
      eventTrigger: 'event-popover',
      isUnconfirmed: this.event.isUnconfirmed,
    });
    this.onPopoverHide();
    this.updateStatus();
  }

  @action
  async declineEvent(): Promise<void> {
    this.event.status = EVENTS_STATUSES.DECLINED;
    await this.saveEvent();
    this.usageAnalytics.track('Event Declined', {
      eventTrigger: 'event-popover',
      isUnconfirmed: this.event.isUnconfirmed,
    });
    this.onPopoverHide();
    this.updateStatus();
  }

  @action
  onEventDeleted(): void {
    this.args.refreshEvents();
  }

  @action
  onPopoverHide(): void {
    // Add this to prevent crash when trying to set value on event that might already be unloaded
    try {
      this.isPopoverVisible = false;
      if (this.event) {
        this.event.isPopoverActive = false;
      }
    } catch (error) {
      this.errorTracking.addBreadcrumb({ data: { error } });
    }
  }

  @action
  onPopoverShow(): void {
    if (this.event) {
      this.event.isPopoverActive = true;
    }

    this.usageAnalytics.track('Event Viewed', {
      eventTrigger: 'event-bar',
      isUnconfirmed: this.event?.isUnconfirmed,
    });
  }

  @action
  resetEvent(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.event.destroyRecord().then(() => {
      this.store.unloadRecord(this.event);

      this.onEventDeleted();
    });

    this.onPopoverHide();
  }

  @action
  async saveEvent(): Promise<void> {
    await this.event.save();
  }

  @action
  updateStatus(): void {
    this.args.refreshEvents();
  }

  <template>
    <div
      class="event-bar truncate
        {{this.event.status}}
        {{if this.event.isUnconfirmed ' unconfirmed'}}
        {{if this.isRealStart ' event-start'}}
        {{if this.isRealEnd ' event-end'}}
        {{if this.isPopoverActive ' is-popover-active'}}
        {{if this.isPastEvent ' is-past-event'}}"
      data-test-id="event-bar"
      style={{this.gridStyle}}
    >

      {{#if this.event.category}}
        <PrismIcon
          @color="inherit"
          @name={{this.event.icon}}
          @size="extra-small"
          class="mr-100"
        />
      {{/if}}
      <GeneralEventName
        @event={{this.event}}
        @strikethrough={{or this.isCancelled this.isRejected}}
        class="text-200--regular w-full"
      />
      {{#if this.isNew}}
        <p class="text-200--bold ml-50">{{this.newTag}}</p>
      {{/if}}

      <EmberPopover
        @delay={{100}}
        @event="click"
        @isShown={{this.isPopoverVisible}}
        @onHide={{this.onPopoverHide}}
        @onShow={{this.onPopoverShow}}
        @side="bottom"
        @spacing={{10}}
        @tooltipClass="events-calendar-popover"
      >
        <HotelEventsTooltipsTooltipEvent
          @acceptEvent={{this.acceptEvent}}
          @declineEvent={{this.declineEvent}}
          @event={{this.event}}
          @hidePopover={{this.onPopoverHide}}
          @isLoading={{@isLoading}}
          @resetEvent={{this.resetEvent}}
        />
      </EmberPopover>
    </div>
  </template>
}
